import {
    faChevronLeft,
    faChevronRight,
    faDownload,
    faTrash,
    faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DOWNLOAD_IMAGES_URL, EVENT_IMAGES_URL, EVENT_VIDEOS_URL, EVENT_NEW_MEDIA_NOTIF } from '../ApiDetails.js';
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import JSZip from "jszip";
import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import defaultImage from "../../images/default.jpg";
import useFormContext from "./useFormEventMltComplete.js";
import ConfirmationModalDeleteUpload from "../ConfirmationModalDeleteUpload.js"

const UploadFilesMlt = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [apiFiles, setApiFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [filter, setFilter] = useState("all"); // Default filter is 'all'
    const [modalFile, setModalFile] = useState(null); // To store the file for enlargement
    const [allSelected, setAllSelected] = useState(false);

    const filesPerPage = 6;
    const { eventFormData, setEventFormData, setFiles, files, showConfirmationModalDeleteUpload, setShowConfirmationModalDeleteUpload } = useFormContext();

    // Function to fetch images
    const fetchImages = async () => {
        try {
            const response = await axios.get(
                `${EVENT_IMAGES_URL}/${eventFormData.eventId}`
            );
            return response.data || [];
        } catch (error) {
            console.error("Error fetching images:", error);
            return [];
        }
    };

    // Function to fetch videos
    const fetchVideos = async () => {
        try {
            const response = await axios.get(`${EVENT_VIDEOS_URL}/${eventFormData.eventId}`);
            return response.data || [];
        } catch (error) {
            console.error("Error fetching videos:", error);
            return [];
        }
    };

    // Function to fetch both images and videos
    const fetchFiles = async () => {
        if (!eventFormData.eventId) return;

        try {
            const [images, videos] = await Promise.all([
                fetchImages(),
                fetchVideos(),
            ]);

            setApiFiles([...images, ...videos]);
        } catch (error) {
            console.error("Error fetching files:", error);
        }
    };

    // Fetch files when eventId is available
    useEffect(() => {
        fetchFiles();
    }, [eventFormData.eventId]);

    const handleFileUpload = async (e) => {
      const uploadedFiles = Array.from(e.target.files);
      setFiles([...files, ...uploadedFiles]);
      
      const sendUploadNotification = async () => {
        try {
          const result = await axios.get(EVENT_NEW_MEDIA_NOTIF+"/"+eventFormData.eventId);
          return result.data;
        } catch (error) {
          console.error(`Error sending media notification`, error);
          return false;
        }
      };

      const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append("EventId", eventFormData.eventId);
        formData.append("File", file);

        const isVideo = file.type.startsWith("video/");
        const url = isVideo ? EVENT_VIDEOS_URL : EVENT_IMAGES_URL;
        try {
          const newEventStatus = await axios.post(url, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              Accept: "text/plain",
            },
          });
          if (newEventStatus && (newEventStatus.data) && (newEventStatus.data!=="")) {
            setEventFormData(prevData => ({ ...prevData, eventStatus: newEventStatus.data }));
          }
        } catch (error) {
          console.error(`Error uploading file: ${file.name}`, error);
        }
      };

      try {
        await Promise.all(uploadedFiles.map(uploadFile));
        const resNotif = await sendUploadNotification();
        alert("All files uploaded successfully");
        if (!resNotif) alert("Error sending new media notification.");

        fetchFiles();
      } catch (error) {
        console.error("Error during file upload:", error);
      }
    };

    const displayFile = (file) => {
        const fileExtension = file.documentLink.split(".").pop().toLowerCase();

        let fileType = "unknown";
        if (["jpg", "jpeg", "png", "gif"].includes(fileExtension)) {
            fileType = "image";
        } else if (["mp4", "mov", "avi", "mkv"].includes(fileExtension)) {
            fileType = "video";
        } else if (["pdf", "doc", "docx", "txt"].includes(fileExtension)) {
            fileType = "document";
        }

        if (fileType === "image") {
            return (
                <img
                    src={file.mediaLink}
                    alt=""
                    className="img-thumbnail"
                    style={{ maxHeight: "100%"}}
                />
            );
        } else if (fileType === "video") {
            return (
                <video
                    controls
                    style={{ maxHeight: "100%"}}
                >
                    <source src={file.mediaLink} />
                    Your browser does not support the video tag.
                </video>
            );
        } else {
            return (
                <a href={file.mediaLink} target="_blank" rel="noopener noreferrer">
                    {file.documentLink}
                </a>
            );
        }
    };

    // Handle file deletion for selected files
    const handleDeleteSelected = async () => {
        setShowConfirmationModalDeleteUpload(false);
        const filesToDelete = apiFiles.filter((file) =>
            selectedFiles.includes(file.mediaID)
        );
        if (selectedFiles.length === 0) {
            alert("No files selected for deletion.");
            return;
        }

        // Loop through the selected files and delete each one
        try {
            await Promise.all(
                filesToDelete.map(async (file) => {
                    const fileExtension = file.documentLink
                        .split(".")
                        .pop()
                        .toLowerCase();

                    if (["mp4", "mov", "avi", "mkv"].includes(fileExtension)) {
                        // Delete video
                        await axios.delete(`${EVENT_VIDEOS_URL}/${file.mediaID}`);
                    } else if (
                        [
                            "jpg",
                            "jpeg",
                            "png",
                            "gif",
                            "pdf",
                            "doc",
                            "docx",
                            "xls",
                            "xlsx",
                            "txt",
                        ].includes(fileExtension)
                    ) {
                        // Delete image
                        await axios.delete(`${EVENT_IMAGES_URL}/${file.mediaID}`);
                    }
                })
            );
            alert("Selected files have been deleted.");
            setSelectedFiles([]);
            setAllSelected(false);
            fetchFiles();
        } catch (error) {
            console.error("Error deleting files:", error);
        }
    };

    // Toggle file selection with checkbox
    const toggleFileSelection = (index) => {
        if (selectedFiles.includes(index)) {
            setSelectedFiles(selectedFiles.filter((i) => i !== index));
        } else {
            setSelectedFiles([...selectedFiles, index]);
        }
        setAllSelected(false);
    };

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedFiles([]);
        } else {
            setSelectedFiles(filteredFiles.map(file => file.mediaID));
        }

        setAllSelected(!allSelected);
    };

    // Enlarge file (open modal)
    const enlargeFile = (file) => {
        setModalFile(file); // Store the file to show in the modal
    };

    // Close modal
    const closeModal = () => {
        setModalFile(null);
    };

    // Handle filter change from dropdown
    const handleFilterChange = (event) => {
        setFilter(event.target.value);
        setCurrentPage(0);
    };

    // Filter files based on the selected dropdown option
    const filteredFiles = React.useMemo(() => {
        return apiFiles.filter((file) => {
            const fileExtension = file.documentLink.split(".").pop().toLowerCase();
            if (filter === "all") return true;
            if (
                filter === "image" &&
                ["jpg", "jpeg", "png", "gif"].includes(fileExtension)
            )
                return true;
            if (
                filter === "video" &&
                ["mp4", "mov", "avi", "mkv"].includes(fileExtension)
            )
                return true;
            if (
                filter === "document" &&
                ["pdf", "doc", "docx", "txt"].includes(fileExtension)
            )
                return true;
            return false;
        });
    }, [apiFiles, filter]);

    const handlePageClick = (event) => {
        setCurrentPage(event.selected);
    };
    const [sorting, setSorting] = useState("Newest");
    const handleSortingChange = (event) => {
        setSorting(event.target.value);
    };

    const [createBySorting, setCreateBySorting] = useState("All");
    const handlecreateBySortingChange = (event) => {
        setCreateBySorting(event.target.value);
    };
    const [filteredByUser, setFilteredByUser] = useState([]);
    const offset = currentPage * filesPerPage;

    useEffect(() => {
        let filterData2 =
            createBySorting === "All"
                ? [...filteredFiles]
                : filteredFiles.filter((item) => item?.createdBy === createBySorting);

        setFilteredByUser(filterData2);
    }, [filteredFiles, createBySorting]);

    const currentFiles =
        sorting === "Newest"
            ? filteredByUser
                .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
                .slice(offset, offset + filesPerPage)
            : filteredByUser
                .sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate))
                .slice(offset, offset + filesPerPage);
    const pageCount = Math.ceil(filteredByUser.length / filesPerPage);
    const [createdBy, setCreatedBy] = useState([]);

    useEffect(() => {
        const clist = [];
        if (apiFiles?.length > 0) {
            apiFiles?.map((item) => {
                return clist.push(item?.createdBy);
            });
            setCreatedBy([...new Set(clist)]);
        }
    }, [apiFiles]);

    // Group the data by user and date
    const groupedData = currentFiles.reduce((acc, file) => {
        const user = file.createdBy;
        const date = new Date(file.createdDate).toLocaleDateString('en-US', {timeZone: 'UTC'});

        if (!acc[user]) {
            acc[user] = {};
        }

        if (!acc[user][date]) {
            acc[user][date] = [];
        }

        acc[user][date].push(file);

        return acc;
    }, {});

    // Function to handle file download for selected files
    const handleDownloadSelected = async () => {
        const filesToDownload = apiFiles.filter((file) =>
            selectedFiles.includes(file.mediaID)
        );

        if (filesToDownload.length === 0) {
            alert("No files selected for download.");
            return;
        }

        const zip = new JSZip();
        const folder = zip.folder("downloaded_files");

        const downloadPromises = filesToDownload.map(async (file) => {
            try {
                const response = await fetch(file.mediaLink);
                if (!response.ok) {
                    throw new Error(`Failed to fetch ${file.documentLink}`);
                }
                const blob = await response.blob();
                const fileName = file.documentLink.split("/").pop();
                folder.file(fileName, blob);
            } catch (error) {
                console.error("Error downloading file:", file.documentLink, error);
            }
        });

        try {
            await Promise.all(downloadPromises);
            const zipBlob = await zip.generateAsync({ type: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(zipBlob);
            link.download = "selected_files.zip";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setSelectedFiles([]);
            setAllSelected(false);
        } catch (error) {
            console.error("Error generating zip file:", error);
        }
    };

    return (
        <div className="container bg-light rounded p-3 mt-1">
            <div className="file-drop-area">
                <span className="choose-file-button">+</span>
                <span className="file-message col-md-6">
                    Click here to upload files{" "}
                    <div>or drag & drop files in this zone</div>
                </span>

                <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="file-input mb-1"
                    accept="image/*, video/*, .pdf, .doc, .docx, .txt"
                />
            </div>

            {/* Filter Dropdown */}
            <div className="row g-3 mt-1 align-items-center summaryFiltersContainer">
                <div className="col-auto">
                    <div className="input-group">
                        <span className="input-group-text">Type</span>
                        <select
                            id="filterDropdown"
                            value={filter}
                            onChange={handleFilterChange}
                            className="custom-font-lt form-control form-select form-select-sm"
                            aria-label="Filter by type"
                            name="attendees"
                        >
                            <option value="all">All Files</option>
                            <option value="image">Images</option>
                            <option value="video">Videos</option>
                            <option value="document">Documents</option>
                        </select>
                    </div>
                </div>

                <div className="col-auto">
                    <div className="input-group">
                        <span className="input-group-text">Sent By</span>
                        <select
                            className="custom-font-lt form-control form-select form-select-sm"
                            aria-label="Small select example"
                            id="attendees"
                            name="attendees"
                            value={createBySorting}
                            onChange={handlecreateBySortingChange}
                        >
                            <option value={"All"}>All</option>
                            {createdBy?.map((item) => {
                                return <option value={item} key={item}>{item}</option>;
                            })}
                        </select>
                    </div>
                </div>

                <div className="col-auto">
                    <div className="input-group">
                        <span className="input-group-text">Sort By</span>
                        <select
                            className="custom-font-lt form-control form-select form-select-sm"
                            aria-label="Small select example"
                            id="attendees"
                            name="attendees"
                            value={sorting}
                            onChange={handleSortingChange}
                        >
                            <option value={"Newest"}>Newest</option>
                            <option value={"Oldest"}>Oldest</option>
                        </select>
                    </div>
                </div>

                <div className="col-auto">
                    <div className="input-group">
                        <span className="input-group-text">Select all</span>
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => toggleSelectAll()}
                        />
                    </div>
                </div>

                <div className="col ml-auto">
                    <div className="d-flex summaryDeleteDownloadIconContainer">
                        <FontAwesomeIcon
                            icon={faDownload}
                            size="lg"
                            onClick={handleDownloadSelected}
                            style={{
                                cursor: "pointer",
                                color: "#0000a0",
                                fontSize: "15px",
                            }}
                        />
                        <FontAwesomeIcon
                            icon={faTrash}
                            size="lg"
                            className="delete-icon"
                            onClick={() => {if (selectedFiles.length === 0) { alert("No files selected for deletion."); return; } else setShowConfirmationModalDeleteUpload(true);}}
                            style={{
                                cursor: "pointer",
                                color: "#0000a0",
                                fontSize: "15px",
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="row">
                {Object.entries(groupedData).map(([user, dates]) => (
                    <div key={user} className="col-12 mb-2 summaryEachUser">
                        {Object.entries(dates).map(([date, files]) => (
                            <div key={date} className="mb-2 mt-2 summaryEachUserSection">
                                {/* User and Date Section */}
                                <div className="d-flex align-items-center justify-content-between mb-1 summarUserDateContainer">
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon
                                            icon={faUser}
                                            className="me-2"
                                            style={{ fontSize: "20px", color: "#0000a0" }}
                                        />
                                        <div>
                                            <div className="user-name">{user}</div>
                                            <div className="text-muted" style={{ fontSize: "10px" }}>
                                                {date}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Section for Images & Videos */}
                                <div className="row">
                                    {files
                                        .filter((file) => {
                                            const fileExtension = file.documentLink
                                                .split(".")
                                                .pop()
                                                .toLowerCase();
                                            return [
                                                "jpg",
                                                "jpeg",
                                                "png",
                                                "gif",
                                                "mp4",
                                                "mov",
                                                "avi",
                                                "mkv",
                                            ].includes(fileExtension);
                                        })
                                        .map((file, index) => (
                                            <div key={index} className="col-md-4 mb-3 file-container"
                                                style={{ marginTop: "0px", height:"300px", maxHeight:"300px", width:"300px", maxWidth:"300px"}}>
                                                <div className="media-file" style={{ marginTop: "0px", width:"100%", height:"100%", maxHeight:"100%", justifyContent:"center", alignItems:"center"}}>
                                                    <div style={{ marginTop: "0px", height:"95%", maxHeight:"95%", display:"flex", justifyContent:"center", alignItems:"center"}}
                                                        onClick={() => enlargeFile(file)}>
                                                    {displayFile(file)}
                                                    </div>
                                                    <div className="mt-2 d-flex justify-content-center">
                                                        <input
                                                            style={{ index: "2", marginLeft: "15px" }}
                                                            type="checkbox"
                                                            checked={selectedFiles.includes(file?.mediaID)}
                                                            onChange={() =>
                                                                toggleFileSelection(file?.mediaID)
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>

                                {/* Section for Document Files */}
                                <div className="row">
                                    {files
                                        .filter((file) => {
                                            const fileExtension = file.documentLink
                                                .split(".")
                                                .pop()
                                                .toLowerCase();
                                            return [
                                                "pdf",
                                                "doc",
                                                "docx",
                                                "xls",
                                                "xlsx",
                                                "txt",
                                            ].includes(fileExtension);
                                        })
                                        .map((file, index) => (
                                            <div key={index} className="file-container">
                                                <div className="document-file d-flex align-items-center summaryDocContainer">
                                                    <div className="file-indicator"></div>
                                                    <div className="d-flex justify-content-between align-items-center w-100">
                                                        <a
                                                            href={file.mediaLink}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="document-link"
                                                        >
                                                            {file.documentLink.split("_").join(" ")}
                                                        </a>
                                                        <input
                                                            style={{ index: "2" }}
                                                            type="checkbox"
                                                            checked={selectedFiles.includes(file?.mediaID)}
                                                            onChange={() =>
                                                                toggleFileSelection(file?.mediaID)
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {apiFiles.length > filesPerPage && (
                <ReactPaginate
                    previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
                    nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
                    pageCount={pageCount}
                    onPageChange={handlePageClick}
                    containerClassName={"pagination float-end"}
                    pageClassName={"page-item"}
                    pageLinkClassName={"page-link"}
                    previousClassName={"page-item"}
                    previousLinkClassName={"page-link"}
                    nextClassName={"page-item"}
                    nextLinkClassName={"page-link"}
                    activeClassName={"active"}
                />
            )}

            {/* Modal for Enlarged File */}
            {modalFile && (
            <div style={{ display:"flex", marginTop:"0px", width:"100%", height:"100%", maxHeight:"100%", minHeight:"100vh", justifyContent:"center", alignItems:"center", textAlign:"center"}}>
            <div
                className="modal fade show posteventSummaryFileEnlargedView"
                style={{ display: "flex", maxWidth:"100%", maxHeight:"93%", height:"93%"}}
                role="dialog"
                onClick={closeModal}
            >
                <div className="modal-content"
                    style={{ height:"100%", maxHeight:"100%"}}
                >
                    <div className="modal-header" style={{ paddingBottom: "5px" }}>
                        <button type="button" className="btn-close" onClick={closeModal}></button>
                    </div>
                    <div className="media-file" style={{ display:"flex", marginTop: "0px", height:"95%", maxHeight:"95%", justifyContent:"center", alignItems:"center"}}>
                        {displayFile(modalFile)}
                    </div>
                </div>
            </div>
            </div>
            )}
          <ConfirmationModalDeleteUpload
            isOpen={showConfirmationModalDeleteUpload}
            onClose={() => setShowConfirmationModalDeleteUpload(false)}
            onSave={handleDeleteSelected}
            onCancel={() => setShowConfirmationModalDeleteUpload(false)}
            nbUploads={selectedFiles.length}
          />
        </div>
    );
};

export default UploadFilesMlt;